import * as Headless from '@headlessui/react';
import TextareaAutosize, { type TextareaAutosizeProps } from 'react-textarea-autosize';
import clsx from 'clsx';
import React, { forwardRef } from 'react';

const textareaClasses = [
  // Basic layout
  'relative block h-full w-full appearance-none rounded-md px-[calc(--spacing(3.5)-1px)] py-[calc(--spacing(2.5)-1px)] sm:px-[calc(--spacing(3)-1px)] sm:py-[calc(--spacing(1.5)-1px)]',
  // Typography
  'text-base/6 text-foreground placeholder:text-muted-foreground sm:text-sm/6',
  // Border
  'border border-border data-hover:border-border',
  // Background color
  'bg-transparent dark:bg-muted/30',
  // Hide default focus styles
  'focus:outline-hidden',
  // Invalid state
  'data-invalid:border-destructive data-invalid:data-hover:border-destructive',
  // Disabled state
  'disabled:border-border',
  // Resize
  'resize-none',
];

function WrapperSpan({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <span
      data-slot="control"
      className={clsx([
        className,
        // Basic layout
        'relative block w-full',
        // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
        'before:absolute before:inset-px before:rounded-[calc(var(--radius-md)-1px)] before:bg-card before:shadow-sm',
        // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
        'dark:before:hidden',
        // Focus ring
        'after:pointer-events-none after:absolute after:inset-0 after:rounded-md after:ring-transparent after:ring-inset sm:focus-within:after:ring-2 sm:focus-within:after:ring-primary',
        // Disabled state
        'has-data-disabled:opacity-50 has-data-disabled:before:bg-muted has-data-disabled:before:shadow-none',
      ])}
    >
      {children}
    </span>
  );
}

interface TextareaProps extends Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'className' | 'style'
> {
  className?: string;
  style?: React.CSSProperties;
  invalid?: boolean;
  minRows?: number;
  maxRows?: number;
}

export const Textarea = forwardRef(function Textarea(
  { className, minRows, maxRows, invalid, style, ...props }: TextareaProps,
  ref: React.ForwardedRef<HTMLTextAreaElement>
) {
  // When auto-resize is requested, use react-textarea-autosize
  if (minRows !== undefined || maxRows !== undefined) {
    return (
      <WrapperSpan className={className}>
        <TextareaAutosize
          ref={ref}
          minRows={minRows}
          maxRows={maxRows}
          data-invalid={invalid || undefined}
          {...props}
          style={style as TextareaAutosizeProps['style']}
          className={clsx(textareaClasses)}
        />
      </WrapperSpan>
    );
  }

  // Default: use Headless UI Textarea for Field integration
  return (
    <WrapperSpan className={className}>
      <Headless.Textarea
        ref={ref}
        invalid={invalid}
        {...props}
        rows={props.rows ?? 3}
        style={{ resize: 'none' as const, ...style }}
        className={clsx(textareaClasses)}
      />
    </WrapperSpan>
  );
});
